/**
 * useWorkflowOverviewKpis - Contadores do topo da página Processos (Workflow)
 *
 * Lê a RPC segura `get_workflow_overview_kpis` (tenant validado no banco):
 * pendências abertas, vencidas, bloqueantes, minhas e erros abertos.
 *
 * @version 1.0.0
 * RELEASE: 2026-10-06 v1.0.0 - Criação inicial (submenus do Workflow).
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';

const logger = createLogger('useWorkflowOverviewKpis');

/** Contadores devolvidos pela RPC */
export interface WorkflowOverviewKpis {
  pendencias_abertas: number;
  pendencias_vencidas: number;
  pendencias_bloqueantes: number;
  minhas_pendencias: number;
  erros_abertos: number;
}

const VAZIO: WorkflowOverviewKpis = {
  pendencias_abertas: 0,
  pendencias_vencidas: 0,
  pendencias_bloqueantes: 0,
  minhas_pendencias: 0,
  erros_abertos: 0,
};

export function useWorkflowOverviewKpis() {
  const { currentTenant } = useAuth();
  const tenantId = currentTenant?.id;

  const query = useQuery({
    queryKey: ['workflow-overview-kpis', tenantId],
    enabled: !!tenantId,
    staleTime: 15_000,
    queryFn: async (): Promise<WorkflowOverviewKpis> => {
      const { data, error } = await (supabase.rpc as any)('get_workflow_overview_kpis', {
        p_tenant_hint: tenantId ?? null,
      });
      if (error) {
        logger.error('Falha ao carregar contadores do workflow', { error: error.message });
        throw error;
      }
      return { ...VAZIO, ...(data ?? {}) } as WorkflowOverviewKpis;
    },
  });

  return { kpis: query.data ?? VAZIO, isLoading: query.isLoading };
}
